(ns nl.mediquest.questionnaire-frontend.component.surveyjs.renderers.rating-widget
  (:require
   [clojure.string :as str]
   [nl.mediquest.questionnaire-frontend.component.i18n :refer [text-or-i18n]]
   [reagent.core :as reagent]
   [survey-core :refer [RendererFactory Serializer]]
   [survey-react-ui :refer [ReactQuestionFactory]]))

(defn- add-properties-to-question
  "Add custom properties to the rating question type for SurveyJS 2.0 compatibility"
  []
  (.addProperty Serializer "rating"
                #js {:name "hasNaOptions"
                     :type "boolean"
                     :default false})
  (.addProperty Serializer "rating"
                #js {:name "naOptions"
                     :itemType "itemvalue"
                     :type "itemvalue[]"})
  (.addProperty Serializer "rating"
                #js {:name "images"
                     :type "file"})
  (.addProperty Serializer "rating"
                #js {:name "hasImages"
                     :type "boolean"
                     :default false}))

(defn- make-options [rate-min rate-max]
  (for [i (range rate-min (inc rate-max))]
    {:description i :value i}))

(defn- make-na-options
  "Convert the na-options to a map of description and value.

  SurveyJS 2.0 expects a text and a value for the itemvalue type.
  This type is relevant when adding customizations in the SurveyJS Creator.
  For backwards compatibility, we support getting the description
  from the JSON payload if no text was given."
  [na-options]
  (for [option na-options]
    (let [description (.. option -jsonObj -description)]
      {:description (if description
                      (js->clj description :keywordize-keys true)
                      (js->clj (.-text option) :keywordize-keys true))
       :value (.-value option)})))

(defn- options-container
  ([name options on-change selected-value]
   (options-container name options on-change selected-value []))
  ([name options on-change selected-value extra-classes]
    [:div.flex.gap-2.flex-wrap
     (doall
      (for [{:keys [value description] :as _option} options]
        [:label
         {:key (str name "-" value)}
         [:div.sd-rating__item
          {:class (conj extra-classes
                        (if (= selected-value value)
                          "sd-rating__item--selected"
                          "sd-rating__item--allowhover"))}
          [:input.sv-visuallyhidden
           {:type "radio"
            :name name
            :on-click (fn [_e] (on-change (when-not (= selected-value value) value)))
            :value value}]
          [:span.sd-rating__item-text [text-or-i18n description]]]]))]))

(defn- na-options-container
  "Container for not available (niet van toepassing) options,
   with SurveyJS 2.0 compatible styling.

   Note that these lack the class `sd-rating__item--fixed-size`
   in order to fit longer words."
  [name options on-change selected-value]
  [:div.flex.gap-2.flex-wrap
   (doall
    (for [{:keys [value description] :as _option} options]
      ^{:key (str name "-" value)}
      [:label
       [:div.sd-rating__item.sd-rating__item--custom-na-option
        {:class [(if (= selected-value value)
                   "sd-rating__item--selected"
                   "sd-rating__item--allowhover")]}
        [:input.sv-visuallyhidden
         {:type "radio"
          :name name
          :on-click (fn [_e] (on-change (if (= selected-value value) nil value)))
          :value value}]
        [:span.sd-rating__item-text.ml-auto
         [text-or-i18n description]]]]))])

(defn- preview-na-options-container
  "Container for not available (niet van toepassing) options,
   with SurveyJS 2.0 compatible styling,
   for the preview possibly shown at the end of a questionnaire."
  [name options selected-value]
  [:<>
   (doall
    (for [{:keys [value description] :as _option} options]
      ^{:key (str name "-" value)}
      [:div.sd-rating__item.sd-rating__item--preview
       {:style (when (= selected-value value)
                 {:color "var(--sjs-general-backcolor, var(--background, #ffffff))"
                  :background-color "var(--sjs-general-forecolor, var(--foreground, #161616))"})}
       [:span [text-or-i18n description]]]))])

(defn images-rating-widget
  "Custom rating widget with images (e.g smileys)
  shown above the labels"
  [selected-value on-change ^js props]
  (let [question-model (.-question props)
        preview? (.-isDisplayMode props)
        name (.-name question-model)
        rate-min (or (.-rateMin question-model) 1)
        rate-max (.-rateMax question-model)
        min-rate-description (.-minRateDescription question-model)
        max-rate-description (.-maxRateDescription question-model)
        options (make-options rate-min rate-max)
        images (.. question-model -jsonObj -Images) ]
    [:div.sd-scrollable-container.sd-rating
     [:fieldset {:role "radiogroup"}
      [:div.flex.items-center.gap-x-12.gap-y-6.flex-wrap
       [:div.flex.gap-x-4.gap-y-6.items-center.flex-wrap
        [:legend.sv-hidden {:role "presentation"}]
        (when (not (str/blank? min-rate-description))
          [:span.sd-rating__item-text.sd-rating__min-text [text-or-i18n min-rate-description]])
        [:div.flex.flex-col.gap-2.flex-wrap
         [:div
          {:class ["hidden" "sm:flex" "sm:justify-between"]}
          (doall
           (for [image-url images]
             [:img {:key (str name image-url)
                    :style (when preview? {:filter "grayscale(100%)"})
                    :class "w-6"
                    :src image-url}]))]
         [options-container name options on-change selected-value
          ["sd-rating__item--fixed-size" (if preview? "sd-rating__item--preview" "")]]]
        (when (not (str/blank? max-rate-description))
          [:span.sd-rating__item-text.sd-rating__max-text [text-or-i18n max-rate-description]])]]]]))

(defn- RatingWidget
  "A custom rating widget extending the SurveyJS rating component with
   extra features, compatible with the preview possibly shown
   at the end of a questionnaire."
  [^js props]
  (let [^js question-model (.-question props)
        selected-value (.-value question-model)
        preview? (.-isDisplayMode props)
        name (.-name question-model)
        has-na-options (.-hasNaOptions question-model)
        na-options (make-na-options (.-naOptions question-model))
        has-images (.-hasImages question-model)
        on-change (fn [val]
                    (set! (.-value question-model) val)
                    (when (.-onValueChanged question-model)
                      (.onValueChanged question-model)))]
    (reagent/as-element
     [:div.flex.gap-x-12.gap-y-4.items-center.py-2.flex-wrap
      (if has-images
        [images-rating-widget selected-value on-change props]
        (.. ReactQuestionFactory -Instance (createQuestion "rating" props)))
      (when has-na-options
        (if preview?
          [preview-na-options-container name na-options selected-value]
          [na-options-container name na-options on-change selected-value]))])))

(defn- install-rating-widget-renderer!
  "Register the custom rating renderer, its custom properties, and make
  it the default for all `rating` questions.

  Must run before any `survey-core/Model` is constructed, otherwise
  questions are bound to the stock rating renderer at creation time and
  our custom component (which draws the NA options) never runs."
  []
  (add-properties-to-question)
  (.. ReactQuestionFactory
      -Instance
      (registerQuestion "sv-rating-widget" RatingWidget))
  (.. RendererFactory
      -Instance
      (registerRenderer "rating" "rating-widget" "sv-rating-widget"))
  (when-let [rating-property (-> Serializer (.findProperty "rating" "renderAs"))]
    (set! (.-defaultValue rating-property) "rating-widget")))

(defonce ^:private installed
  (do (install-rating-widget-renderer!) true))
